import React from "react";
import {
  FiSidebar,
  FiEdit,
  FiSearch,
  FiSettings,
  FiTrash,
  FiClipboard,
  FiRefreshCw,
} from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router";

import * as types from "types";
import { countActiveTasks } from "./utilities/countActiveTasks";
import {
  shouldStopPolling,
  getTaskKind,
  completionDotLabel,
} from "types/TaskRegistry";
import type { Task } from "types/Task";
import { getUnreadCompletionIndicator } from "types/Task";
import { buildThreadTaskIndicators } from "./utilities/threadTaskIndicators";
import { useSetAtom } from "jotai";
import {
  pendingInjectionsAtom,
  removePendingInjectionsForChat,
} from "hooks/tasks/usePendingInjections";
import { TaskListPopup } from "components/TaskList";
import styles from "./ChatHistory.module.scss";
import { deleteLegacyFolder as ccDeleteLegacyFolder } from "services/cortevaChain/methods/deleteFolder";
import { deleteChatThread as ccDeleteChatThread } from "services/cortevaChain/methods/deleteChatThread";
import EditableChatHistoryElement from "./components/EditableChatHistoryElement";
import ReadOnlyChatHistoryElement from "./components/ReadOnlyChatHistoryElement";
import ThreadListSkeleton from "./components/ThreadListSkeleton";
import { SettingsModal } from "../Modal";
import SearchModal from "./SearchModal/SearchModal";
import { exportThreadToJson } from "services/dbApi/utilities/exportImport";
import { groupThreadsByDate } from "./utilities/groupThreadsByDate";
import { deleteThreadResources } from "./utilities/deleteThreadResources";
import useGetAccessToken from "components/Login/useGetAccessToken";
import useGetMetricsToken from "services/metricsApi/hooks/useGetMetricsToken";
import { redactChatRecord } from "services/metricsApi/methods/redactChatRecord";

import useThreadsQuery from "services/dbApi/hooks/useThreadsQuery";
import usePatchThreadMutation from "services/dbApi/hooks/usePatchThreadMutation";
import useDeleteThreadMutation from "services/dbApi/hooks/useDeleteThreadMutation";
import { getThreadMessages } from "services/dbApi/methods/getThreadMessages";
import useTasksQuery from "services/dbApi/hooks/useTasksQuery";

/**
 * Returns long-task jobs that are non-terminal and live in any of the chats
 * being deleted. Surfaced in the confirmation so the user can back out.
 */
function collectActiveLongTasks(jobs: Task[], chatIds: string[]): Task[] {
  const chatIdSet = new Set(chatIds);
  return jobs.filter(
    (j) =>
      getTaskKind(j) === "long-task" &&
      !j.dismissed &&
      !shouldStopPolling(j.status) &&
      chatIdSet.has(j.threadId)
  );
}

/**
 * Confirmation text for deleting chats that still have tasks running. `scope`
 * distinguishes deleting a single chat from the multi-select delete, whose
 * tasks may span several chats.
 */
function buildLongTaskWarning(jobs: Task[], scope: "one" | "many"): string {
  const count = jobs.length;
  const location = scope === "one" ? "in this chat" : "in the selected chats";
  const subject =
    count === 1
      ? `1 task ${location} is still running`
      : `${count} tasks ${location} are still running`;
  const target = scope === "one" ? "the chat" : "the chats";
  const objectPronoun = count === 1 ? "it" : "them";
  const possessive = count === 1 ? "its" : "their";
  return (
    `${subject}. Deleting ${target} won't stop ${objectPronoun}: ` +
    `${objectPronoun} will keep running on the server, but you won't be ` +
    `able to see ${possessive} results.`
  );
}

function ChatHistory() {
  const navigate = useNavigate();
  const getAccessToken = useGetAccessToken();
  const getMetricsToken = useGetMetricsToken();

  const threadsQuery = useThreadsQuery();
  // Prevents skeleton flash on retry: React Query resets isLoading to true on refetch
  // after error. errorUpdatedAt (not failureCount) stays stable during retry.
  const hasFailedOnce = threadsQuery.errorUpdatedAt > 0;

  const patchThreadMutation = usePatchThreadMutation();
  const deleteThreadMutation = useDeleteThreadMutation();
  const tasksQuery = useTasksQuery();
  const params = useParams();

  const chatThreads: types.ChatThread[] = threadsQuery.data ?? [];
  const tasks: Task[] = tasksQuery.data ?? [];

  // Provide ref to EditableChatHistoryElement for positioning on ChatHistory scroll
  const threadListRef = React.useRef<HTMLUListElement>(null);
  // The chat being viewed, null if the user is on the "new chat" page.
  const activeChatId = params.chatId ?? null;

  // Scroll the thread list to the top when the active chat is bumped to the
  // top of "Recents" after sending a message. usePutMessagesMutation's
  // onSuccess writes a new timestamp into the cache, which causes
  // useThreadsQuery to re-render this component with an updated activeTimestamp.
  // The effect below compares it against prevTimestampRef to detect the change.
  // prevChatIdRef is needed to distinguish "same chat got a new timestamp"
  // (scroll) from "user clicked a different chat" (don't scroll — the
  // different chat has a different timestamp, but that's not a bump).
  const activeThread = chatThreads.find((thread) => thread.id === activeChatId);
  const activeTimestamp = activeThread?.timestamp;
  const prevTimestampRef = React.useRef(activeTimestamp);
  const prevChatIdRef = React.useRef(activeChatId);

  React.useEffect(() => {
    const chatChanged = activeChatId !== prevChatIdRef.current;
    if (
      !chatChanged &&
      activeTimestamp &&
      activeTimestamp !== prevTimestampRef.current
    ) {
      threadListRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
    prevTimestampRef.current = activeTimestamp;
    prevChatIdRef.current = activeChatId;
  }, [activeTimestamp, activeChatId]);
  // We only want to allow the user to edit one chat at a time.
  // This is the chat ID of the chat that is currently being edited.
  const [chatIdBeingEdited, setChatIdBeingEdited] = React.useState<
    string | null
  >(null);
  const [showSearchModal, setShowSearchModal] = React.useState(false);
  // State for controlling the settings modal
  const [showSettingsModal, setShowSettingsModal] = React.useState(false);
  // State for controlling the task list popup
  const [showTaskList, setShowTaskList] = React.useState(false);
  const taskListButtonRef = React.useRef<HTMLButtonElement>(null);
  // State for controlling collapsed sidebar - initialized from localStorage
  const [isCollapsed, setIsCollapsed] = React.useState(() => {
    const saved = localStorage.getItem("chatHistoryCollapsed");
    return saved === "true";
  });
  // Multi-select state: set of selected chat thread IDs
  const [selectedThreadIds, setSelectedThreadIds] = React.useState<Set<string>>(
    new Set()
  );
  const hasSelection = selectedThreadIds.size > 0;
  const setPendingInjections = useSetAtom(pendingInjectionsAtom);

  // One pass for the whole sidebar. Keyed on the query data rather than the
  // `tasks` fallback, whose `?? []` yields a fresh array each render and would
  // defeat the memo.
  const threadTaskIndicators = React.useMemo(
    () => buildThreadTaskIndicators(tasksQuery.data ?? []),
    [tasksQuery.data]
  );

  const toggleTaskList = () => {
    setShowTaskList((prev) => !prev);
  };

  // Persist collapsed state to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem("chatHistoryCollapsed", String(isCollapsed));
  }, [isCollapsed]);

  // When the user clicks a chat element's edit button, set the active chat ID.
  const onEditButtonClick = (chatId: string) => {
    setChatIdBeingEdited(chatId);
  };

  // Cancel editing a chatThread. Nothing is saved.
  const onCancelButtonClick = () => {
    setChatIdBeingEdited(null);
  };

  // Update the chatThread's name.
  const onSaveButtonClick = (chatId: string, newChatName: string) => {
    patchThreadMutation.mutate({
      threadId: chatId,
      patch: { name: newChatName },
    });

    // Reset the active chat ID.
    setChatIdBeingEdited(null);
  };

  const deleteOneThread = async (chatId: string) => {
    await deleteThreadResources({
      deleteLegacyFolder: () => ccDeleteLegacyFolder(chatId, getAccessToken),
      deleteChatThread: () => ccDeleteChatThread(chatId, getAccessToken),
      redactChatRecord: () => redactChatRecord(chatId, getMetricsToken),
      deleteThread: () => deleteThreadMutation.mutateAsync(chatId),
    });
    // The dbApi delete's onSuccess evicts the thread's threads/messages/tasks
    // from the React Query cache; drop the chat's queued directives from the
    // in-memory injection queue too so a deleted chat leaves nothing behind.
    setPendingInjections((prev) =>
      removePendingInjectionsForChat(prev, chatId)
    );
  };

  const onDeleteButtonClick = (chatId: string) => {
    const activeLongTasks = collectActiveLongTasks(tasks, [chatId]);
    const baseMessage = "Are you sure you want to delete this chat?";
    const confirmDelete = window.confirm(
      activeLongTasks.length > 0
        ? `${baseMessage}\n\n${buildLongTaskWarning(activeLongTasks, "one")}`
        : baseMessage
    );
    if (!confirmDelete) return;

    deleteOneThread(chatId)
      .then(() => {
        // Reset the active chat ID.
        setChatIdBeingEdited(null);

        // If the chat being deleted is the active chat, navigate to the home page.
        if (chatId === activeChatId) {
          navigate("/");
        }
      })
      .catch(() => {
        console.error(`${chatId} deletion failed`);
      });
  };

  // Delete all chats
  const onDeleteAllChats = async () => {
    if (chatThreads === undefined) return;

    // Get all chat IDs
    const chatIds = chatThreads.map((t) => t.id);
    if (chatIds.length === 0) return;
    await Promise.allSettled(chatIds.map((chatId) => deleteOneThread(chatId)));

    // Reset the active chat ID
    setChatIdBeingEdited(null);

    // Navigate to home page
    navigate("/");
  };

  // Export the currently active chat thread to a JSON file.
  const handleExportCurrentChat = activeChatId
    ? async () => {
        if (chatThreads === undefined) return;

        const chatThread = chatThreads.find((t) => t.id === activeChatId);
        if (chatThread === undefined) return;

        const messagesResponse = await getThreadMessages(
          chatThread.id,
          getAccessToken
        );

        exportThreadToJson(chatThread, messagesResponse.messages);
      }
    : undefined;

  // Settings modal handlers
  const openSettingsModal = () => setShowSettingsModal(true);
  const closeSettingsModal = () => setShowSettingsModal(false);

  // Toggle collapsed state
  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
    setSelectedThreadIds(new Set());
  };

  // Track the last-clicked thread index for shift-click range selection
  const lastClickedIndexRef = React.useRef<number | null>(null);

  // Multi-select: toggle a single thread, or shift-click to select a range
  const toggleThreadSelection = (chatId: string, shiftKey: boolean) => {
    const flatIds = groupedChats.flatMap(([, chats]) => chats.map((t) => t.id));
    const currentIndex = flatIds.indexOf(chatId);

    if (shiftKey && lastClickedIndexRef.current !== null) {
      const start = Math.min(lastClickedIndexRef.current, currentIndex);
      const end = Math.max(lastClickedIndexRef.current, currentIndex);
      const rangeIds = flatIds.slice(start, end + 1);
      const selecting = !selectedThreadIds.has(chatId);
      setSelectedThreadIds((prev) => {
        const next = new Set(prev);
        for (const id of rangeIds) {
          if (selecting) {
            next.add(id);
          } else {
            next.delete(id);
          }
        }
        return next;
      });
    } else {
      setSelectedThreadIds((prev) => {
        const next = new Set(prev);
        if (next.has(chatId)) {
          next.delete(chatId);
        } else {
          next.add(chatId);
        }
        return next;
      });
    }

    lastClickedIndexRef.current = currentIndex;
  };

  const onDeleteSelectedClick = async () => {
    const ids = Array.from(selectedThreadIds);
    if (ids.length === 0) return;

    const activeLongTasks = collectActiveLongTasks(tasks, ids);
    const baseMessage = `Are you sure you want to delete ${ids.length} chat${
      ids.length > 1 ? "s" : ""
    }?`;
    const confirmDelete = window.confirm(
      activeLongTasks.length > 0
        ? `${baseMessage}\n\n${buildLongTaskWarning(
            activeLongTasks,
            ids.length === 1 ? "one" : "many"
          )}`
        : baseMessage
    );
    if (!confirmDelete) return;

    await Promise.allSettled(ids.map((chatId) => deleteOneThread(chatId)));

    // Clear selection
    setSelectedThreadIds(new Set());
    setChatIdBeingEdited(null);

    // If the active chat was deleted, navigate home
    if (activeChatId && selectedThreadIds.has(activeChatId)) {
      navigate("/");
    }
  };

  //
  // Render
  //

  // Group chat threads by date.
  const groupedChats = groupThreadsByDate(chatThreads);

  const allCount = Object.keys(chatThreads).length;
  const allSelected = hasSelection && selectedThreadIds.size === allCount;
  const isIndeterminate = hasSelection && !allSelected;
  const selectAllRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const selectAllCheckbox = (
    <span
      className={styles.checkboxLabel}
      onClick={() => {
        if (hasSelection) {
          setSelectedThreadIds(new Set());
        } else {
          const allIds = groupedChats.flatMap(([, chats]) =>
            chats.map((t) => t.id)
          );
          setSelectedThreadIds(new Set(allIds));
        }
      }}
    >
      <input
        ref={selectAllRef}
        type="checkbox"
        className={`${styles.selectAllCheckbox}${
          hasSelection ? "" : ` ${styles.hoverOnly}`
        }`}
        checked={allSelected}
        readOnly
      />
    </span>
  );

  const listItems = groupedChats.map(([group, chats], groupIndex) => {
    const chatListItems = chats.map((chatThread) => {
      const chatId = chatThread.id;
      const chatListElement =
        chatId === chatIdBeingEdited ? (
          <EditableChatHistoryElement
            threadListRef={threadListRef}
            chatId={chatId}
            chatName={chatThread.name}
            onSave={onSaveButtonClick}
            onCancel={onCancelButtonClick}
            onDelete={onDeleteButtonClick}
            onOutsideClick={onCancelButtonClick}
          />
        ) : (
          <ReadOnlyChatHistoryElement
            active={chatId === activeChatId}
            chatId={chatId}
            chatName={chatThread.name}
            onEditButtonClick={onEditButtonClick}
            isSelected={selectedThreadIds.has(chatId)}
            hasSelection={hasSelection}
            onToggleSelect={toggleThreadSelection}
            taskIndicator={threadTaskIndicators.get(chatId)}
          />
        );

      return <li key={chatId}>{chatListElement}</li>;
    });
    return (
      <li id={group} key={group}>
        <div className={styles.dateRow}>
          <p className={styles.dateText}>{group}</p>
          {groupIndex === 0 && selectAllCheckbox}
        </div>
        <ul>{chatListItems}</ul>
      </li>
    );
  });

  let threadListContent: React.ReactNode;
  if (threadsQuery.isLoading && !hasFailedOnce) {
    // First load, no prior error -> show skeleton.
    threadListContent = <ThreadListSkeleton />;
  } else if (hasFailedOnce && !threadsQuery.data?.length) {
    // Has errored + no cached data -> retry UI.
    threadListContent = (
      <li className={styles.threadListError}>
        <p className={styles.errorMessage}>Couldn&apos;t load your chats</p>
        <button
          type="button"
          onClick={() => threadsQuery.refetch()}
          className={styles.retryButton}
          disabled={threadsQuery.isFetching}
        >
          <FiRefreshCw
            aria-hidden="true"
            className={
              threadsQuery.isFetching ? styles.retryIconSpinning : undefined
            }
          />
          <span>{threadsQuery.isFetching ? "Loading…" : "Retry"}</span>
        </button>
      </li>
    );
  } else {
    // Otherwise (including empty []) -> show threads.
    threadListContent = listItems;
  }

  const activeTaskCount = countActiveTasks(tasks);
  const { hasUnread, hasFailed } = getUnreadCompletionIndicator(tasks);

  return (
    <aside
      className={`${styles.chatHistory} ${isCollapsed ? styles.collapsed : ""}`}
    >
      <div className={styles.historyHeader}>
        <button
          onClick={toggleCollapsed}
          className={`${styles.toggleButton} ${styles.primaryButton}`}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <FiSidebar />
        </button>
        <Link to="/" className={styles.primaryButton} key="new">
          <FiEdit />
          {!isCollapsed && <span className={styles.buttonText}>New Chat</span>}
        </Link>
        {chatThreads.length > 0 && (
          <>
            <button
              onClick={() => setShowSearchModal(true)}
              className={styles.primaryButton}
            >
              <FiSearch />
              <span className={styles.buttonText}>Search Chats</span>
            </button>
            {!isCollapsed && <h3 className={styles.recentsHeading}>Recents</h3>}
          </>
        )}
      </div>
      <ul ref={threadListRef} className={styles.threadList}>
        {threadListContent}
      </ul>
      <div className={styles.settingsFooter}>
        {hasSelection && (
          <button
            onClick={onDeleteSelectedClick}
            className={`${styles.primaryButton} ${styles.deleteSelectedButton}`}
          >
            <FiTrash />
            <span className={styles.buttonText}>
              Delete Selected ({selectedThreadIds.size})
            </span>
          </button>
        )}
        <div
          className={styles.footerButtons}
          style={
            isCollapsed
              ? { flexDirection: "column", alignItems: "center" }
              : undefined
          }
        >
          <button
            onClick={openSettingsModal}
            className={`${styles.primaryButton} ${styles.settingsButton}`}
            style={
              isCollapsed
                ? {
                    flex: "0 0 auto",
                    width: "2.5rem",
                    height: "2.5rem",
                    padding: 0,
                    justifyContent: "center",
                  }
                : undefined
            }
          >
            <FiSettings />
            {!isCollapsed && (
              <span className={styles.buttonText}>Settings</span>
            )}
          </button>
          <button
            ref={taskListButtonRef}
            onClick={toggleTaskList}
            className={`${styles.primaryButton} ${styles.taskListButton}`}
            style={isCollapsed ? { height: "2.5rem", padding: 0 } : undefined}
            aria-label={
              hasUnread
                ? `View tasks — ${completionDotLabel(hasFailed)}`
                : "View tasks"
            }
            title="Tasks"
          >
            <FiClipboard />
            {activeTaskCount > 0 && (
              <span className={styles.badge}>{activeTaskCount}</span>
            )}
            {hasUnread && (
              // Purely decorative: the host button's `aria-label` replaces its
              // subtree for name computation and `.taskCompletionDot` is
              // pointer-transparent, so a name or title here would be inert.
              <span
                aria-hidden="true"
                className={`${styles.taskCompletionDot} ${
                  hasFailed ? styles.completionFailed : styles.completionSuccess
                }`}
              />
            )}
          </button>
        </div>
      </div>
      {showTaskList && (
        <TaskListPopup
          buttonRef={taskListButtonRef}
          onClose={() => setShowTaskList(false)}
        />
      )}
      {showSearchModal && (
        <SearchModal onClose={() => setShowSearchModal(false)} />
      )}
      {showSettingsModal && (
        <SettingsModal
          onClose={closeSettingsModal}
          onDeleteAllChats={onDeleteAllChats}
          onExportCurrentChat={handleExportCurrentChat}
          getAccessToken={getAccessToken}
        />
      )}
    </aside>
  );
}

export default React.memo(ChatHistory);
